import { useCallback } from 'react'
import { useStore } from '../store/useStore'
import { api } from '../services/api'

// #1190 Phase 4: auto top-up は「常にクレジットカード即時決済」なので、
// FEE_CONFIG の env 依存料率ではなく、実 Stripe card 料率をハードコードで
// 使う (env が 0 のときも sheet に「¥350 + 2%」を明示、客が納得の上で承認)。
//
// 2026-07 更新: 4.4% → 2% に引下げ。外国人客が cash-only 店の補完用途で
// 使えるラインに揃える (弊社は ~¥19k 超のチャージでは赤字になり得るが、
// 顧客獲得コストとして許容。prod #1202 の実 Stripe 統合でこの値と一致さ
// せる)。
export const AUTO_TOPUP_CARD_FEE_FIXED = 350
export const AUTO_TOPUP_CARD_FEE_RATE = 0.02

/** クレジットカード即時 top-up の手数料計算 (¥350 + amount * 4.4%)。 */
export function calculateAutoTopupCardFee(topupAmount: number): number {
  if (topupAmount <= 0) return 0
  return AUTO_TOPUP_CARD_FEE_FIXED + Math.floor(topupAmount * AUTO_TOPUP_CARD_FEE_RATE)
}

/**
 * #1190 Phase 4: YOKOSO 通貨払い + 残高不足時 auto top-up の hook。
 *
 * 客が支払時に「YOKOSO 通貨」を選び、残高が不足していたら
 * 「不足分だけチャージ → 全額支払う」を 1 tap で完結させる。
 *
 * デモ用途限定 (isYokosoPointsEnabled() で feature flag gate)。実運用の Phase 2/3
 * 経路は非同期完了 (bank_transfer / redirect) で 1 tap には合わないため、
 * backend の /payment/execute-with-topup は APP_ENV=prod で 404 を返す。
 */
export type TopUpBreakdown = {
  /** 現在残高 */
  balance: number
  /** 請求額 (店舗支払 total) */
  billAmount: number
  /** チャージが必要な額 (max(0, billAmount - balance)) */
  topupAmount: number
  /** チャージ手数料 (VITE_CHARGE_FEE_FIXED + VITE_CHARGE_FEE_RATE) */
  topupFee: number
  /** 客が実際に支払う総額 (topupAmount + topupFee) */
  totalCharged: number
  /** 支払後の残高 (常に 0 か billAmount - balance で計算した端数) */
  balanceAfter: number
  /** top-up が不要かどうか (balance >= billAmount なら true) */
  isTopUpNeeded: boolean
}

export function calculateTopUpBreakdown(balance: number, billAmount: number): TopUpBreakdown {
  const isTopUpNeeded = balance < billAmount
  const topupAmount = Math.max(0, billAmount - balance)
  const topupFee = calculateAutoTopupCardFee(topupAmount)
  const totalCharged = topupAmount + topupFee
  const balanceAfter = balance + topupAmount - billAmount
  return {
    balance,
    billAmount,
    topupAmount,
    topupFee,
    totalCharged,
    balanceAfter,
    isTopUpNeeded,
  }
}

export function useAutoTopUp() {
  const restoreSession = useStore((s) => s.restoreSession)

  /**
   * atomic charge + payment を backend の /payment/execute-with-topup で実行。
   * 成功後、restoreSession() で store の残高を最新化する。
   */
  const executeWithTopup = useCallback(
    async (params: {
      storeId: string
      billAmount: number
      topupAmount: number
      topupFee: number
    }) => {
      const res = await api.executePaymentWithTopup({
        store_id: params.storeId,
        amount_jpyc: params.billAmount,
        topup_amount: params.topupAmount,
        topup_fee_jpyc: params.topupFee,
      })
      // 順番重要: restoreSession を先に叩いて、その後 wallet の
      // balanceJPYC を endpoint 返却値で確定させる。restoreSession が非同期で
      // 古い balance を書き込んで setState を上書きするのを防ぐ。
      try { await restoreSession() } catch { /* best-effort */ }
      if (typeof res?.new_balance === 'number') {
        useStore.setState((state) => ({
          wallet: state.wallet
            ? { ...state.wallet, balanceJPYC: res.new_balance }
            : state.wallet,
        }))
      }
      return res
    },
    [restoreSession],
  )

  return { executeWithTopup }
}
